import { SmartyCubeStorageKeys } from '@core/config/smartycube-storage-keys';
import { defaultGeneralSettings } from '@core/constant';
import { DateFormat, TableColumns } from '@smartera/core';
import { getFormatedDate, groupDataByFields } from '@core/utils';
import { prepareFormatByLabelMetaData } from '@feature/smart-reports-core/formatter';
import moment from 'moment';
import { environment } from '../../../../environments/environment';
import {
	AggregationField,
	AggregationMethod,
	ConditionalFormatting,
	DateFilterQuery,
	FieldStyleConfigs,
	FilterQuery,
	NumberFilterRangeQuery,
	NumberFilterTermsQuery,
	ScriptAggregations,
	SearchFilter,
	SearchFilters,
	TextFilterMatchQuery,
	TextFilterTermsQuery,
} from '../models';
import { IndexFieldMetaData } from '../models/field';
import { LabelMetaData } from '../models/label-meta-data';
import { DateFormatType, GeneralSettingsObject, Key } from '../models/type';
import { ESIndex } from '../services/elastic-search-index.service';
import { DatePriority, EmptyDate } from './smart-report-utility';
import {
	FieldConditionalFormatting,
	FieldConditionalFormattingMethod,
} from '@feature/smart-reports-core/components/field-conditional-formatting/field-conditional-formatting.component';

const OTHER_FIELD_SEPARATOR = '╰┄►';

const isDemoMode = environment.demo;

export function prepareReportMetaDataConfig(source: ESIndex): LabelMetaData {
	const localReportMetaData = JSON.parse(localStorage.getItem(SmartyCubeStorageKeys.DEMO_USER_REPORT_META_DATA));
	if (isDemoMode && localReportMetaData && localReportMetaData[source.name]) {
		return JSON.parse(localReportMetaData[source.name]);
	} else {
		return JSON.parse(source.metaData.data) || {};
	}
}

export function prepareReportGeneralSettings(source: ESIndex): GeneralSettingsObject {
	const localGeneralSettings = JSON.parse(
		localStorage.getItem(SmartyCubeStorageKeys.DEMO_USER_REPORT_GENERAL_SETTINGS)
	);
	if (isDemoMode && localGeneralSettings && localGeneralSettings[source.name]) {
		return JSON.parse(localGeneralSettings[source.name]);
	} else {
		if (source.generalSettings && source.generalSettings.data) {
			return JSON.parse(source.generalSettings.data);
		} else {
			return defaultGeneralSettings;
		}
	}
}

/* ElasticSearch Filter Query */

function generateElasticSearchFilterQuery(filter: any, anysData?, lbm?: LabelMetaData): object {
	const must_not = [];
	if (anysData) {
		const fields = [...anysData.columns, ...anysData.rows];
		fields.forEach((field) => {
			if (field.value === lbm[field.key].nullValue) {
				must_not.push({ exists: { field: field.key } });
			}
		});
	}
	let filterData = filter.query;
	if (filterData) {
		filterData = {
			must: [...filterData],
			must_not,
		};
	}
	return {
		bool: {
			...filterData,
		},
	};
}

/* ElasticSearch Aggregation Query */

function generateAggregationQuery(
	fields: Key[],
	aggregationFields: AggregationField[],
	labelMetaData: LabelMetaData
): object {
	const aggregation = {};
	let subAggregation = aggregation;

	for (const field of fields) {
		switch (labelMetaData[field.name].type) {
			case 'date':
				subAggregation[field.name] = prepareDateFieldAggregationQuery(field, labelMetaData);
				break;
			case 'text':
				subAggregation[field.name] = prepareTextFieldAggregationQuery(field, labelMetaData);
				break;
			case 'double':
			case 'long':
			case 'float':
				subAggregation[field.name] = prepareNumberFieldAggregationQuery(field, labelMetaData);
				break;
		}

		subAggregation[field.name].aggs = {};
		prepareFieldAggregationOrder(field, subAggregation, aggregationFields, labelMetaData);

		// NOTE: Aggregation'lar iç içe olarak yerleştirilmesi gerekiyor. Bu yüzden bir sonraki field, şu anki field'ın aggs key'inin altında olmalıdır.
		subAggregation = subAggregation[field.name].aggs;
	}
	prepareAggregationMethodQuery(subAggregation, aggregationFields, labelMetaData);
	return aggregation;
}

interface OtherFilter {
	[key: string]: string[] | string;
}

function generateOtherAggregationQuery(
	fields: Key[],
	aggregationFields: AggregationField[],
	labelMetaData: LabelMetaData,
	data: unknown[]
): object {
	const otherFieldIndex = fields.findIndex((field: Key) => field?.showOtherValues);
	const otherFields = fields.slice(0, otherFieldIndex + 1);
	const notOtherFields = fields.slice(otherFieldIndex + 1);

	const fieldKeys = [...otherFields.map((field: Key) => field.name)];
	const otherFilterValues = prepareOtherFilterValues(data, fieldKeys);

	const notFilterAggregationQuery = generateAggregationQuery(notOtherFields, aggregationFields, labelMetaData);
	const otherFilterAggregationQuery = prepareOtherFilterAggregationQuery(otherFields, otherFilterValues);

	return {
		other_filter: {
			aggs: notFilterAggregationQuery,
			filters: {
				filters: otherFilterAggregationQuery,
			},
		},
	};
}

function prepareOtherFilterAggregationQuery(otherFields: Key[], otherFilterValues: OtherFilter[]): object {
	const filters = {};
	otherFilterValues.forEach((otherFilter) => {
		const filter = [];
		const mustNot = [];
		let filterKey = '';
		otherFields.forEach((field: Key, index: number) => {
			if (index < otherFields.length - 1) {
				filter.push({
					match_phrase: {
						[`${field.name}.keyword`]: otherFilter[field.name],
					},
				});
				filterKey += OTHER_FIELD_SEPARATOR + otherFilter[field.name];
			} else {
				filter.push({
					exists: {
						field: `${field.name}.keyword`,
					},
				});
				(otherFilter[field.name] as Array<string>).forEach((value: string) => {
					mustNot.push({
						match_phrase: {
							[`${field.name}.keyword`]: value,
						},
					});
				});
			}
		});
		filters[filterKey] = {
			bool: {
				must: [],
				filter: filter,
				should: [],
				must_not: mustNot,
			},
		};
	});
	return filters;
}

function prepareOtherFilterValues(data: unknown[], fields: string[]): OtherFilter[] {
	const [currentField, ...nextFields] = fields;
	const otherFilterValues = [];

	if (currentField) {
		const groupedData = groupDataByFields(data, [currentField]);
		const currentFieldValues: string[] = [];
		groupedData.forEach((value: unknown[], key: string) => {
			currentFieldValues.push(key);
		});

		if (nextFields.length > 0) {
			currentFieldValues.forEach((currentFieldValue: string) => {
				const subData = groupedData.get(currentFieldValue);
				const subOtherFilterValues = prepareSubOtherFilterValues(currentField, currentFieldValue, subData, nextFields);
				if (Array.isArray(subOtherFilterValues)) {
					otherFilterValues.push(...subOtherFilterValues);
				} else {
					otherFilterValues.push(subOtherFilterValues);
				}
			});
		} else {
			const filterValue = {};
			filterValue[currentField] = currentFieldValues;
			otherFilterValues.push(filterValue);
		}
	}
	return otherFilterValues;
}

/**
 * Gelen parametreye göre satır veya sütun başlıklarının alt başlıklarını oluşturan method.
 */
function prepareSubOtherFilterValues(
	baseField: string,
	baseFieldValue: string,
	data: unknown[],
	fields: string[]
): OtherFilter | OtherFilter[] {
	const [currentField, ...nextFields] = fields;

	const groupedData = groupDataByFields(data, [currentField]);
	const currentFieldValues: string[] = [];
	groupedData.forEach((value: unknown[], key: string) => {
		currentFieldValues.push(key);
	});

	let otherFilterValues = {};
	otherFilterValues[currentField] = [];

	if (nextFields.length > 0) {
		const subOtherFilterValues = [];
		currentFieldValues.forEach((currentFieldValue: string) => {
			const subData = groupedData.get(currentFieldValue);
			otherFilterValues = prepareSubOtherFilterValues(currentField, currentFieldValue, subData, nextFields);
			otherFilterValues[currentField] = currentFieldValue;
			otherFilterValues[baseField] = baseFieldValue;
			subOtherFilterValues.push(otherFilterValues);
		});
		return subOtherFilterValues;
	} else {
		otherFilterValues[currentField] = currentFieldValues;
		otherFilterValues[baseField] = baseFieldValue;
		return otherFilterValues;
	}
}

function prepareAggregationMethodQuery(
	aggregation: object,
	aggregationFields: AggregationField[],
	labelMetaData: LabelMetaData
): void {
	aggregationFields
		.filter(({ method, field }) => method && field)
		.forEach(({ method, field }, index) => {
			const id = getAggregationFieldMethodId(field, index);

			if (!aggregation[id]) {
				aggregation[id] = {};
			}

			if (method === AggregationMethod.CARDINALITY && labelMetaData[field].type === 'text') {
				aggregation[id][method] = {
					field: field + '.keyword',
				};
			} else if (method === AggregationMethod.VALUE_COUNT) {
				// Adet gösteriminde null değerleri saymadığı için boş olan değerleri 0 olarak setleyerek getirecek.
				aggregation[id][method] = {
					field: field,
					missing: 0,
				};
			} else if (ScriptAggregations.includes(method)) {
				if (index >= 2) {
					aggregation[id] = {
						bucket_script: {
							buckets_path: {
								n_1: getAggregationFieldMethodId(aggregationFields[index - 2].field, index - 2),
								n: getAggregationFieldMethodId(aggregationFields[index - 1].field, index - 1),
							},
							script: getAggregationScript(method),
						},
					};
				}
			} else {
				aggregation[id][method] = {
					field: field,
				};
			}
		});
}

function getAggregationScript(aggregationMethod: AggregationMethod | string): string {
	switch (aggregationMethod) {
		case AggregationMethod.PERCENTAGE_N:
			return '(params.n / params.n_1)';
		case AggregationMethod.PERCENTAGE_N_1:
			return '(params.n_1 / params.n)';
	}
}

function getAggregationFieldMethodId(fieldName: string, index: number): string {
	return `${fieldName}_${index}`;
}

function prepareTextFieldAggregationQuery(field: Key, labelMetaData: LabelMetaData): object {
	return {
		terms: {
			field: `${field.name}.keyword`,
			size: field.size ?? 100,
			missing: labelMetaData[field.name].nullValue,
		},
	};
}

function prepareNumberFieldAggregationQuery(field: Key, labelMetaData: LabelMetaData): object {
	return {
		terms: {
			field: `${field.name}`,
			size: field.size ?? 100,
			missing: labelMetaData[field.name].nullValue,
		},
	};
}

function prepareDateFieldAggregationQuery(field: Key, labelMetaData: LabelMetaData): object {
	return {
		date_histogram: {
			field: `${field.name}`,
			format: prepareDateFormat(field, labelMetaData),
			calendar_interval: field.dateFormat.interval,
			missing: prepareDateFieldMissingValue(field, labelMetaData, field.dateFormat),
			min_doc_count: 1,
		},
	};
}

function prepareFieldAggregationOrder(
	field: Key,
	aggregation: object,
	aggregationFields: AggregationField[],
	labelMetaData: LabelMetaData
): void {
	if (field.orderBy) {
		// aggregation'da yer alan terms ve date_histogram işlemleri için sıralamayı ekler
		const order = {
			[`${field.orderBy}`]: field.order,
		};
		switch (labelMetaData[field.name].type) {
			case 'date':
				aggregation[field.name].date_histogram['order'] = order;
				break;
			case 'text':
			case 'double':
			case 'long':
			case 'float':
				aggregation[field.name].terms['order'] = order;
				break;
		}

		// yukarıda belirtilen sıralamaya göre aggregation'ınına sıralamayı ekler.
		const aggregationFieldIndex = field.orderBy.slice(-1);
		const aggregationField: AggregationField = aggregationFields[aggregationFieldIndex];
		aggregation[field.name].aggs[field.orderBy] = {};

		switch (labelMetaData[aggregationField.field].type) {
			case 'text':
				aggregation[field.name].aggs[field.orderBy][aggregationField.method] = {
					field: `${aggregationField.field}.keyword`,
				};
				break;
			case 'double':
			case 'long':
			case 'float':
				aggregation[field.name].aggs[field.orderBy][aggregationField.method] = {
					field: aggregationField.field,
				};
				break;
		}
	}
}

/*
	TODO: Refactor edilecek!!
	Label Meta Data ile bağlantılı, tarih alanlarının missing value değerlerini oluştururken farklı bir yöntem izlenilmesi daha uygun olacaktır.
*/
function prepareDateFieldMissingValue(field: Key, labelMetaData: LabelMetaData, dateFormat: DateFormatType) {
	let missing: string;
	const { name } = field;
	const { interval } = dateFormat;

	const number1 = makeNumberTwoDigit(labelMetaData[name]['number1']);
	const number2 = makeNumberTwoDigit(labelMetaData[name]['number2']);
	const number3 = makeNumberTwoDigit(labelMetaData[name]['number3']);

	if (labelMetaData[name]['isMissingValueEmpty']) {
		switch (labelMetaData[name]['format']) {
			case DatePriority.dayFirst:
				switch (interval) {
					case 'year':
						missing = EmptyDate.year;
						return missing;
					case 'month':
						missing = `${EmptyDate.month}/${EmptyDate.year}`;
						return missing;
					case 'day':
						missing = `${EmptyDate.day}/${EmptyDate.month}/${EmptyDate.year}`;
						return missing;
					case 'week':
						missing = `${EmptyDate.week}/${EmptyDate.year}`;
						return missing;
				}
				break;
			case DatePriority.monthFirst:
				switch (interval) {
					case 'year':
						missing = EmptyDate.year;
						return missing;
					case 'month':
						missing = `${EmptyDate.month}/${EmptyDate.year}`;
						return missing;
					case 'day':
						missing = `${EmptyDate.month}/${EmptyDate.day}/${EmptyDate.year}`;
						return missing;
					case 'week':
						missing = `${EmptyDate.week}/${EmptyDate.year}`;
						return missing;
				}
				break;
			case DatePriority.yearFirst:
				switch (interval) {
					case 'year':
						missing = EmptyDate.year;
						return missing;
					case 'month':
						missing = `${EmptyDate.year}/${EmptyDate.month}`;
						return missing;
					case 'day':
						missing = `${EmptyDate.year}/${EmptyDate.month}/${EmptyDate.day}`;
						return missing;
					case 'week':
						missing = `${EmptyDate.year}/${EmptyDate.week}`;
						return missing;
				}
				break;
		}
	} else {
		switch (labelMetaData[name]['format']) {
			case DatePriority.dayFirst:
				switch (interval) {
					case 'year':
						missing = number3;
						return missing;
					case 'month':
						missing = `${number2}/${number3}`;
						return missing;
					case 'day':
						missing = `${number1}/${number2}/${number3}`;
						return missing;
					case 'week':
						missing = `${number2}/${number3}`;
						return missing;
				}
				break;
			case DatePriority.monthFirst:
				switch (interval) {
					case 'year':
						missing = number3;
						return missing;
					case 'month':
						missing = `${number2}/${number3}`;
						return missing;
					case 'day':
						missing = `${number2}/${number1}/${number3}`;
						return missing;
					case 'week':
						missing = `${number2}/${number3}`;
						return missing;
				}
				break;
			case DatePriority.yearFirst:
				switch (interval) {
					case 'year':
						missing = number3;
						return missing;
					case 'month':
						missing = `${number3}/${number2}`;
						return missing;
					case 'day':
						missing = `${number3}/${number2}/${number1}`;
						return missing;
					case 'week':
						missing = `${number3}/${number2}`;
						return missing;
				}
				break;
		}
	}
}

function makeNumberTwoDigit(number: number): string {
	if (number < 10) {
		return '0' + number.toString();
	}
	return number.toString();
}

function prepareDateFormat(field: Key, labelMetaData: LabelMetaData) {
	let format = '';
	const { interval } = field.dateFormat;
	switch (interval) {
		case 'day':
			switch (labelMetaData[field.name]['format']) {
				case DatePriority.dayFirst:
					format = 'dd/MM/yyyy';
					break;

				case DatePriority.monthFirst:
					format = 'MM/dd/yyyy';
					break;

				case DatePriority.yearFirst:
					format = 'yyyy/MM/dd';
					break;
			}
			break;
		case 'month':
			switch (labelMetaData[field.name]['format']) {
				case DatePriority.dayFirst:
				case DatePriority.monthFirst:
					format = 'MM/yyyy';
					break;
				case DatePriority.yearFirst:
					format = 'yyyy/MM';
					break;
			}
			break;
		case 'year':
			format = 'yyyy';
			break;
		case 'week':
			switch (labelMetaData[field.name]['format']) {
				case DatePriority.dayFirst:
				case DatePriority.monthFirst:
					format = 'ww/yyyy';
					break;
				case DatePriority.yearFirst:
					format = 'yyyy/ww';
					break;
			}
			break;
	}
	return format;
}

/* ------------------------------- */

/* ElasticSearch Search Query */

function generateSearchQuery(
	indexFieldsMetaData: IndexFieldMetaData,
	searchFilters: SearchFilters,
	searchFieldsKeys: string[]
): FilterQuery {
	const query = [];
	for (const key of searchFieldsKeys) {
		if (searchFilters[key]) {
			const filter: SearchFilter = searchFilters[key];
			filter.operator = filter.operator ?? 'match';
			let filterQuery = null;
			switch (indexFieldsMetaData[key].type) {
				case 'date':
					filterQuery = prepareDateFieldQuery(key, filter);
					break;
				case 'text':
					filterQuery = prepareTextFieldQuery(key, filter);
					break;
				case 'double':
				case 'float':
				case 'long':
					filterQuery = prepareNumberFieldQuery(key, filter);
					break;
			}
			if (filterQuery) {
				query.push(filterQuery);
			}
		}
	}
	return query;
}

function prepareTextFieldQuery(key: string, filter: SearchFilter): TextFilterMatchQuery | TextFilterTermsQuery {
	if (filter.firstSearchPart != null) {
		switch (filter.operator) {
			case 'term':
				return {
					terms: {
						[`${key}.keyword`]: filter.firstSearchPart as string,
					},
				};
			case 'match':
				return {
					query_string: {
						query: `*${filter.firstSearchPart}*`,
						fields: [key],
						default_operator: 'AND',
					},
				};
		}
	}
}

function prepareNumberFieldQuery(key: string, filter: SearchFilter): NumberFilterRangeQuery | NumberFilterTermsQuery {
	if (filter.firstSearchPart != null) {
		switch (filter.operator) {
			case 'term':
				return {
					terms: {
						[key]: [filter.firstSearchPart as number],
					},
				};
			case 'range':
				return {
					range: {
						[key]: {
							gte: filter.firstSearchPart as number,
							lte: filter.lastSearchPart as number,
						},
					},
				};
		}
	}
}

function prepareDateFieldQuery(key: string, filter: SearchFilter): DateFilterQuery {
	let gte: string | Date;
	let lte: string | Date;
	if (filter.firstSearchPart != null) {
		switch (filter.operator) {
			case 'term':
			case 'match':
				gte = getFormatedDate(filter.firstSearchPart as Date);
				lte = getFormatedDate(filter.firstSearchPart as Date);
				break;
			case 'range':
				gte = getFormatedDate(filter.firstSearchPart as Date);
				lte = getFormatedDate(filter.lastSearchPart as Date);
				break;
			case 'dynamic':
				gte = filter.firstSearchPart as string;
				lte = filter.lastSearchPart as string;
				break;
			case 'indicator-date':
				gte = `#DYNAMIC_DATE{${filter.firstSearchPart}[format=dd/MM/yyyy]}`;
				lte = null;
				break;
		}
		return {
			range: {
				[key]: {
					gte: gte,
					lte: lte,
					format: 'dd/MM/yyyy',
				},
			},
		};
	}
}

/* ------------------------------------------- */

function formatValueByLabelMetaDataAndAggregationMehod(
	value: number,
	aggregation: AggregationField,
	labelMetaData: LabelMetaData
): string {
	if (aggregation) {
		if (aggregation.method === 'value_count' || aggregation.method === 'cardinality') {
			return value.toString();
		} else if (ScriptAggregations.includes(aggregation.method)) {
			return new Intl.NumberFormat('tr-TR', {
				style: 'percent',
				minimumFractionDigits: 0,
				maximumFractionDigits: 2,
			}).format(value ?? 0);
		} else {
			return prepareFormatByLabelMetaData(labelMetaData, aggregation.field).format(value);
		}
	}
}

/* ------------------------------------------- */

/**
 * Gelen parametreye göre satır veya sütun başlıklarını oluşturan method.
 */
function prepareColumnHeaders(data: unknown[], fields: string[], aggregationMethods: AggregationField[]) {
	const [currentField, ...nextFields] = fields;
	const fieldsHeadersValue: { [key: string]: string[] } = {};
	if (currentField) {
		fieldsHeadersValue[currentField] = [];
		const groupedData = groupDataByFields(data, [currentField]);
		const currentFieldValues: string[] = [];
		groupedData.forEach((value: unknown[], key: string) => {
			currentFieldValues.push(key);
		});

		currentFieldValues.forEach((currentFieldValue: string) => {
			if (nextFields.length > 0) {
				const subData = groupedData.get(currentFieldValue);
				prepareSubColumnHeaders(
					fieldsHeadersValue,
					currentField,
					currentFieldValue,
					subData,
					nextFields,
					aggregationMethods
				);
			} else {
				fieldsHeadersValue[currentField].push(...new Array(aggregationMethods.length).fill(currentFieldValue));
			}
		});
	}
	return fieldsHeadersValue;
}

/**
 * Gelen parametreye göre satır veya sütun başlıklarının alt başlıklarını oluşturan method.
 */
function prepareSubColumnHeaders(
	fieldsHeadersValue: { [key: string]: string[] },
	baseField: string,
	baseFieldValue: string,
	data: unknown[],
	fields: string[],
	aggregationMethods: AggregationField[]
): number {
	const [currentField, ...nextFields] = fields;
	let subHeaderRepeatCount: number = 0;

	if (!fieldsHeadersValue[currentField]) {
		fieldsHeadersValue[currentField] = [];
	}

	const groupedData = groupDataByFields(data, [currentField]);
	const currentFieldValues: string[] = [];
	groupedData.forEach((value: unknown[], key: string) => {
		currentFieldValues.push(key);
	});

	currentFieldValues.forEach((currentFieldValue: string) => {
		if (nextFields.length > 0) {
			const subData = groupedData.get(currentFieldValue);
			subHeaderRepeatCount += prepareSubColumnHeaders(
				fieldsHeadersValue,
				currentField,
				currentFieldValue,
				subData,
				nextFields,
				aggregationMethods
			);
		} else {
			// Sütun başlıkları oluşturulurken aggregation kadar tekrar etmesi gerekli!
			fieldsHeadersValue[currentField].push(...new Array(aggregationMethods.length).fill(currentFieldValue));
			subHeaderRepeatCount = currentFieldValues.length * aggregationMethods.length;
		}
	});
	fieldsHeadersValue[baseField].push(...new Array(subHeaderRepeatCount).fill(baseFieldValue));
	return subHeaderRepeatCount;
}

/* ------------------------------------------- */

function formatTableHeader(field: Key, value: string): string {
	switch (field.type) {
		case 'text':
			return formatTextHeader(field, value);
		case 'date':
			return formatDateHeader(field, value);
		default:
			return value;
	}
}

/**
 * NOTE: moment.js format'ında dd -> Tue (Günün adını) gösteriyor. Bu sebeple dd veya mm formatlarını büyük harfe çevirmek gerekiyor.
 */
function formatDateHeader(field: Key, value: string): string {
	if (field.dateFormat) {
		return moment(value).format(field.dateFormat.format.toUpperCase());
	} else {
		return value;
	}
}

function formatTextHeader(field: Key, value: string): string {
	if (field.textFormat) {
		return value.length > field.textFormat.maxLength && field.textFormat.maxLength !== null
			? value.substring(0, field.textFormat.maxLength) + '..'
			: value;
	} else {
		return value;
	}
}

function prepareDataGridTableColumns(columnList: string[], labelMetaData: LabelMetaData): TableColumns<any> {
	return columnList.map((column: string) => {
		if (labelMetaData[column]) {
			const { type, label, align } = labelMetaData[column];
			switch (type) {
				case 'date':
					return {
						field: column,
						header: label || column.replaceAll('_', ' '),
						textAlign: align,
						type: 'date',
						dateFormat: labelMetaData[column]?.format ?? DateFormat.FORMAT_9,
					};
				case 'number':
				case 'long':
				case 'float':
				case 'double': {
					const { numberOfDecimal, currency, isPercentage } = labelMetaData[column];
					return {
						field: column,
						header: label || column.replaceAll('_', ' '),
						textAlign: align,
						type: 'number',
						numberFormat: `2.${numberOfDecimal}-${numberOfDecimal}`,
						numberType: isPercentage ? 'percent' : currency && currency !== 'none' ? 'currency' : 'default',
					};
				}
				default:
					return {
						field: column,
						header: label || column.replaceAll('_', ' '),
						textAlign: align,
					};
			}
		} else {
			return {
				field: column,
				header: column.replaceAll('_', ' '),
			};
		}
	});
}

function getLabelFromLabelMetaData(field: string, labelMetaData: LabelMetaData): string {
	if (field) {
		if (labelMetaData[field].label !== null && labelMetaData[field].label !== '') {
			return labelMetaData[field].label;
		} else {
			return field.replaceAll('_', ' ');
		}
	}
}

function cleanFormatNumber(value: string | number): number {
	if (typeof value === 'string') {
		const cleanedInput = value.replace(/\./g, '');
		return parseFloat(cleanedInput);
	} else {
		return value;
	}
}

function checkCondition(value: string, condition: ConditionalFormatting): boolean {
	switch (condition.methodName) {
		case 'less-than':
			return cleanFormatNumber(value) < condition.value;
		case 'less-than-equal':
			return cleanFormatNumber(value) <= condition.value;
		case 'equal':
			return cleanFormatNumber(value) == condition.value;
		case 'greater-than':
			return cleanFormatNumber(value) > condition.value;
		case 'greater-than-equal':
			return cleanFormatNumber(value) >= condition.value;
	}
}

function prepareAggregationFieldStyle(
	value: unknown,
	aggregationField: AggregationField,
	configs: FieldStyleConfigs
): object {
	let style: object = {};
	if (aggregationField?.conditionalFormattingConfiguration) {
		aggregationField.conditionalFormattingConfiguration.forEach((configuration) => {
			if (value && checkCondition(value as string, configuration)) {
				style = {
					backgroundColor: configuration.fontColor,
					fontSize: configuration.fontSize,
					fontStyle: configuration.fontStyle,
				};
			} else {
				style = prepareDefaultAggregationFieldStyleConfigs(aggregationField, configs);
			}
		});
	} else {
		style = prepareDefaultAggregationFieldStyleConfigs(aggregationField, configs);
	}
	return style;
}

function prepareDefaultAggregationFieldStyleConfigs(
	aggregationField: AggregationField,
	configs: FieldStyleConfigs
): object {
	return {
		backgroundColor: configs[aggregationField?.field]?.cellColor,
		color: configs[aggregationField?.field]?.cellTextColor,
		fontSize: configs[aggregationField?.field]?.cellFontSize,
		fontWeight: configs[aggregationField?.field]?.cellFontWeight,
	};
}

function prepareFieldStyle(value: unknown, field: Key, configs: FieldStyleConfigs): object {
	let style: object = {};
	if (field?.conditionalFormattingConfiguration) {
		for (const configuration of field.conditionalFormattingConfiguration) {
			const condition = checkStyleCondition(value as string, field, configuration);
			if (value && condition) {
				style = {
					backgroundColor: configuration.fontColor,
					fontSize: configuration.fontSize,
					fontStyle: configuration.fontStyle,
				};
				return style;
			} else {
				style = prepareDefaultFieldStyleConfigs(field, configs);
			}
		}
	} else {
		style = prepareDefaultFieldStyleConfigs(field, configs);
	}
	return style;
}

function prepareDefaultFieldStyleConfigs(field: Key, configs: FieldStyleConfigs): object {
	return {
		backgroundColor: configs[field?.name]?.cellColor,
		color: configs[field?.name]?.cellTextColor,
		fontSize: configs[field?.name]?.cellFontSize,
		fontWeight: configs[field?.name]?.cellFontWeight,
	};
}

function checkStyleCondition(value: string, field: Key, condition: FieldConditionalFormatting): boolean {
	switch (field.type) {
		case 'text':
			return checkTextFieldCondition(value, condition);
		case 'double':
			return checkNumberFieldCondition(value, condition);
		case 'date':
			return checkDateFieldCondition(value, condition);
	}
}

function checkNumberFieldCondition(value: string, condition: FieldConditionalFormatting): boolean {
	switch (condition.method) {
		case FieldConditionalFormattingMethod.RANGE:
			return checkRangeCondition(Number(value), condition);
		case FieldConditionalFormattingMethod.EQUALS:
			return value === condition.firstValue;
	}
}

function checkDateFieldCondition(value: string, condition: FieldConditionalFormatting): boolean {
	switch (condition.method) {
		case FieldConditionalFormattingMethod.RANGE:
			condition.firstValue = condition.firstValue ? new Date(<string>condition.firstValue) : null;
			condition.lastValue = condition.lastValue ? new Date(<string>condition.lastValue) : null;
			return checkRangeCondition(new Date(value), condition);
		case FieldConditionalFormattingMethod.EQUALS:
			return value === condition.firstValue;
	}
}

function checkRangeCondition(value: number | Date, condition: FieldConditionalFormatting): boolean {
	if (condition.firstValue && condition.lastValue) {
		return value >= condition.firstValue && value <= condition.lastValue;
	} else if (condition.firstValue) {
		return value >= condition.firstValue;
	} else if (condition.lastValue) {
		return value <= condition.lastValue;
	} else {
		return false;
	}
}

function checkTextFieldCondition(value: string, condition: FieldConditionalFormatting): boolean {
	if (Array.isArray(condition.firstValue)) {
		return condition.firstValue.includes(value);
	} else {
		return value == condition.firstValue;
	}
}

export const ReportUtils = {
	generateElasticSearchFilterQuery,
	generateSearchQuery,
	generateAggregationQuery,
	generateOtherAggregationQuery,
	getAggregationFieldMethodId,
	prepareReportMetaDataConfig,
	prepareReportGeneralSettings,
	formatValueByLabelMetaDataAndAggregationMehod,
	prepareColumnHeaders,
	formatTableHeader,
	prepareDateFormat,
	prepareDataGridTableColumns,
	getLabelFromLabelMetaData,
	prepareAggregationFieldStyle,
	prepareFieldStyle,
	OTHER_FIELD_SEPARATOR,
};
